/** Shared scroll, sticky header, and row highlight styles for app tables (any module). */

/** Bootstrap-aligned grey header (matches app panels / table-light) */
export const SHARED_TABLE_HEADER_BG = '#f8f9fa';
export const SHARED_TABLE_HEADER_COLOR = '#495057';
export const SHARED_TABLE_HEADER_BORDER = '#dee2e6';

export const SHARED_TABLE_SCROLL_HEIGHT = 'calc(100dvh - 260px)';

export const SHARED_TABLE_SCROLL_WRAP_STYLE = {
  maxHeight: SHARED_TABLE_SCROLL_HEIGHT,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  position: 'relative',
};

/** className for scrollable Bootstrap/HTML table wrappers */
export const SHARED_TABLE_SCROLL_WRAP_CLASS = 'shared-table-scroll-wrap';

/** Sticky headers without overriding per-column header background colors */
export const SHARED_TABLE_SCROLL_WRAP_PRESERVE_HEADER_COLORS_CLASS =
  'shared-table-scroll-wrap shared-table-scroll-wrap--preserve-header-colors';

/** className for grid-style tables (e.g. storage activation matrix) */
export const SHARED_GRID_TABLE_SCROLL_WRAP_CLASS = 'shared-grid-table-scroll-wrap';

/** className for click-selected tbody rows */
export const SHARED_TABLE_ROW_SELECTED_CLASS = 'shared-table-row-selected';

/** Inventory Status Overview: two-row thead (location codes on row 2) */
export const INVENTORY_STATUS_OVERVIEW_TABLE_WRAP_CLASS =
  'shared-table-scroll-wrap shared-table-scroll-wrap--preserve-header-colors inventory-status-overview-table-wrap';

const STYLE_ID = 'shared-table-styles-v4';

const SHARED_TABLE_CSS = `
  .shared-data-table .rdt_TableHead {
    position: sticky;
    top: 0;
    z-index: 12;
  }
  .shared-data-table .rdt_TableHeadRow {
    min-height: 42px;
    background-color: ${SHARED_TABLE_HEADER_BG};
  }
  .shared-data-table .rdt_TableCol {
    font-weight: 700;
    background-color: ${SHARED_TABLE_HEADER_BG};
    color: ${SHARED_TABLE_HEADER_COLOR};
    border-bottom: 1px solid ${SHARED_TABLE_HEADER_BORDER};
  }
  .shared-data-table .rdt_TableRow {
    cursor: pointer;
  }
  .shared-data-table .rdt_TableRow:hover {
    cursor: pointer;
  }

  .shared-table-scroll-wrap {
    max-height: ${SHARED_TABLE_SCROLL_HEIGHT};
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
  }
  .shared-table-scroll-wrap .table {
    margin-bottom: 0;
  }
  .shared-table-scroll-wrap .table thead th {
    position: sticky;
    z-index: 6;
    box-shadow: 0 1px 0 ${SHARED_TABLE_HEADER_BORDER};
  }
  .shared-table-scroll-wrap:not(.shared-table-scroll-wrap--preserve-header-colors) .table thead th {
    background-color: ${SHARED_TABLE_HEADER_BG} !important;
    color: ${SHARED_TABLE_HEADER_COLOR};
  }
  .shared-table-scroll-wrap .table thead tr:first-child th {
    top: 0;
    z-index: 8;
  }
  .shared-table-scroll-wrap .table thead tr:nth-child(2) th {
    top: var(--shared-table-head-row2-top, 48px);
    z-index: 7;
  }
  .inventory-status-overview-table-wrap .table thead tr:nth-child(2) th {
    top: var(--inv-overview-head-row1-h, 72px);
    z-index: 44;
    background-color: #fff3cd !important;
    color: #212529 !important;
  }
  .shared-table-scroll-wrap .table tbody tr {
    cursor: pointer;
  }
  .shared-table-scroll-wrap .table.table-hover tbody tr:hover > * {
    --bs-table-color-state: var(--bs-table-hover-color);
    --bs-table-bg-state: #cfe2ff;
  }
  .shared-table-scroll-wrap .table tbody tr.shared-table-row-selected > * {
    --bs-table-bg-state: #b6d4fe;
    --bs-table-accent-bg: #b6d4fe;
    background-color: #b6d4fe !important;
  }

  .shared-grid-table-scroll-wrap {
    max-height: ${SHARED_TABLE_SCROLL_HEIGHT};
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  .shared-grid-table-scroll-wrap thead th {
    position: sticky;
    top: 0;
    z-index: 5;
    background-color: ${SHARED_TABLE_HEADER_BG} !important;
    color: ${SHARED_TABLE_HEADER_COLOR};
    box-shadow: 0 1px 0 ${SHARED_TABLE_HEADER_BORDER};
  }
`;

let stylesInjected = false;

export function ensureSharedTableStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) {
    stylesInjected = true;
    return;
  }
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = SHARED_TABLE_CSS;
  document.head.appendChild(el);
  stylesInjected = true;
}

export const sharedDataTableBaseCustomStyles = {
  table: {
    style: {
      backgroundColor: '#fff',
    },
  },
  headRow: {
    style: {
      backgroundColor: SHARED_TABLE_HEADER_BG,
      minHeight: '42px',
      borderBottom: `1px solid ${SHARED_TABLE_HEADER_BORDER}`,
    },
  },
  headCells: {
    style: {
      backgroundColor: SHARED_TABLE_HEADER_BG,
      color: SHARED_TABLE_HEADER_COLOR,
      fontWeight: 700,
      fontSize: '12px',
    },
  },
  rows: {
    style: {
      minHeight: '40px',
      cursor: 'pointer',
    },
  },
  highlightOnHoverStyle: {
    backgroundColor: '#cfe2ff',
    transitionDuration: '0.12s',
    outline: '1px solid #9ec5fe',
  },
};

export function mergeSharedDataTableCustomStyles(customStyles = {}) {
  const base = sharedDataTableBaseCustomStyles;
  const merged = { ...base, ...customStyles };
  Object.keys(base).forEach((key) => {
    if (customStyles[key]) {
      merged[key] = { ...base[key], ...customStyles[key] };
    }
  });
  return merged;
}
